import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Send, Mail, MessageSquare } from "lucide-react";
import api, { errMessage } from "../lib/api";

export default function BlastBuyersModal({ property, onClose }) {
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [allBuyers, setAllBuyers] = useState([]);
  const [selected, setSelected] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [note, setNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [matchRes, listRes] = await Promise.all([
          api.post("/buyers/match", { property_data: property }),
          api.get("/buyers"),
        ]);
        if (cancelled) return;
        setMatches(matchRes.data.matches || []);
        setAllBuyers(listRes.data || []);
        const sel = {};
        for (const b of matchRes.data.matches || []) sel[b.id] = true;
        setSelected(sel);
      } catch (e) {
        toast.error(errMessage(e, "Could not load buyers"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [property]);

  const visible = showAll ? allBuyers : matches;
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const send = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one buyer");
      return;
    }
    if (!sendEmail && !sendSms) {
      toast.error("Pick at least one channel");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/buyers/blast", {
        property_data: property,
        buyer_ids: selectedIds,
        custom_note: note,
        send_email: sendEmail,
        send_sms: sendSms,
      });
      const d = r.data;
      toast.success(
        `Blasted ${d.sent} buyer(s) · ${d.email_count} email · ${d.sms_count} SMS`
      );
      onClose();
    } catch (e) {
      toast.error(errMessage(e, "Blast failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="blast-buyers-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-2xl w-full max-h-[92vh] overflow-y-auto border border-[--ink] animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[--line] flex items-center justify-between">
          <div>
            <div className="overline">// Blast deal to buyers</div>
            <h2 className="font-display text-xl font-bold tracking-tighter mt-1">
              {property.address}
            </h2>
            <div className="text-xs font-mono text-[--ink-muted] mt-0.5">
              {property.city}, {property.state}
            </div>
          </div>
          <button
            data-testid="close-blast-modal"
            onClick={onClose}
            className="p-2 hover:bg-[--bg-soft] border border-[--line]"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {loading ? (
            <div className="text-sm text-[--ink-muted] py-6 text-center">Loading buyers…</div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs">
                <div className="text-[--ink-muted] font-mono">
                  {matches.length} match{matches.length !== 1 ? "es" : ""} of {allBuyers.length} buyer{allBuyers.length !== 1 ? "s" : ""}
                </div>
                <button
                  type="button"
                  data-testid="toggle-show-all-buyers"
                  onClick={() => setShowAll(!showAll)}
                  className="text-[--accent] hover:underline"
                >
                  {showAll ? "Show matches only" : "Show all buyers"}
                </button>
              </div>

              {visible.length === 0 ? (
                <div className="text-sm text-[--ink-muted] py-8 text-center border border-dashed border-[--line]">
                  {allBuyers.length === 0
                    ? "No buyers yet. Add buyers from the Buyers page first."
                    : "No buyers match this property's profile. Toggle “Show all buyers” to override."}
                </div>
              ) : (
                <div className="border border-[--line] divide-y divide-[--line] max-h-72 overflow-y-auto">
                  {visible.map((b) => (
                    <label
                      key={b.id}
                      data-testid={`blast-buyer-${b.id}`}
                      className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[--bg-soft]"
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[b.id]}
                        onChange={(e) =>
                          setSelected((s) => ({ ...s, [b.id]: e.target.checked }))
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{b.name}</div>
                        <div className="text-xs font-mono text-[--ink-muted] truncate">
                          {[b.email, b.phone].filter(Boolean).join(" · ") || "—"}
                          {b.states?.length ? ` · ${b.states.join("/")}` : ""}
                        </div>
                      </div>
                      {b.channels?.email && (
                        <Mail size={12} strokeWidth={1.5} className="text-[--ink-muted]" />
                      )}
                      {b.channels?.sms && (
                        <MessageSquare size={12} strokeWidth={1.5} className="text-[--ink-muted]" />
                      )}
                    </label>
                  ))}
                </div>
              )}

              <div>
                <label className="block">
                  <span className="text-xs text-[--ink-muted] block mb-1">
                    Custom note (optional — prepended to email & SMS)
                  </span>
                  <textarea
                    data-testid="blast-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    className="w-full text-sm"
                    placeholder="Got this off-market today — first one to deposit gets it."
                  />
                </label>
              </div>

              <div className="flex items-center gap-5 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid="blast-channel-email"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                  />
                  Email
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid="blast-channel-sms"
                    checked={sendSms}
                    onChange={(e) => setSendSms(e.target.checked)}
                  />
                  SMS
                </label>
                <div className="ml-auto text-xs text-[--ink-muted] font-mono">
                  {selectedIds.length} selected
                </div>
              </div>
            </>
          )}
        </div>

        <div className="border-t border-[--line] p-4 flex justify-end gap-2">
          <button
            type="button"
            data-testid="cancel-blast"
            onClick={onClose}
            className="btn-ghost"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="send-blast"
            disabled={sending || loading || selectedIds.length === 0}
            onClick={send}
            className="btn-primary flex items-center gap-2"
          >
            <Send size={14} strokeWidth={1.5} />
            {sending ? "Blasting…" : `Blast to ${selectedIds.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
